import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCar, FaGasPump, FaTachometerAlt } from 'react-icons/fa';
import { TbSteeringWheel } from 'react-icons/tb';
import Skeleton from '../components/ui/Skeleton';
import Divider from '../components/ui/Divider';

function Search() {
  const { search } = useParams();
  const [listing, setListing] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch cars
        const carsResponse = await fetch('/api/listing');
        let carsData = await carsResponse.json();
        carsData = carsData.filter(item => item.visibility === "Active");
        carsData.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Fetch blog posts
        const postsResponse = await fetch('/api/posts');
        let postsData = await postsResponse.json();
        postsData = postsData.filter(item => item.visibility === "Active");
        postsData.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Filter by search query
        const searchLower = search?.toLowerCase() || "";
        if (searchLower) {
          carsData = carsData.filter(item =>
            item.title?.toLowerCase().includes(searchLower) ||
            item.make?.toLowerCase().includes(searchLower) ||
            item.model?.toLowerCase().includes(searchLower)
          );
          postsData = postsData.filter(item =>
            item.title?.toLowerCase().includes(searchLower)
          );
        }

        setListing(carsData);
        setPosts(postsData);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [search]);

  const renderSkeleton = () => (
    <div className="relative shadow-md rounded-lg overflow-hidden p-4">
      <Skeleton className="w-full h-48 mb-2 rounded-md" />
      <Skeleton className="h-5 w-3/4 mb-2" />
    </div>
  );

  const isDataEmpty = listing.length === 0 && posts.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 py-12 md:py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Search Results: <span className="text-[#DC2626]">{search}</span>
          </h1>
          <p className="text-lg text-gray-600">
            Found {listing.length} cars and {posts.length} blog posts
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array(8).fill(null).map((_, index) => (
              <div key={index}>{renderSkeleton()}</div>
            ))}
          </div>
        ) : isDataEmpty ? (
          <div className="text-center mt-20 mb-60">
            <h2 className="text-xl font-bold">No Results Found</h2>
            <p className="text-gray-500">No results found for &quot;{search}&quot;. Please try a different search term.</p>
          </div>
        ) : (
          <>
            {posts.length > 0 && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Blog Posts</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {posts.map((item) => (
                    <div key={item._id} className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                      <Link to={`/blog/${item._id}`}>
                        <img
                          src={item.image || item.thumbnail || 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80'}
                          alt={item.title}
                          className="w-full h-48 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                        />
                      </Link>
                      <div className="p-5">
                        <Link to={`/blog/${item._id}`}>
                          <h2 className="font-display text-lg font-semibold text-gray-900 mb-2 hover:text-[#DC2626] transition-colors line-clamp-2">
                            {item.title}
                          </h2>
                        </Link>
                        <p className="text-sm text-gray-500">
                          {new Date(item.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {listing.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Cars</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {listing.map((item) => (
                    <div key={item._id} className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                      <div className="relative">
                        <div className="overflow-hidden bg-gray-100">
                          <Link to={`/cars/${item._id}`}>
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-56 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                            />
                          </Link>
                          {item.itemCondition === "Used" && (
                            <span className='absolute top-3 left-3 bg-[#DC2626] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg uppercase tracking-wide'>
                              {item.itemCondition}
                            </span>
                          )}
                          <div className="absolute top-3 right-3 bg-[#DC2626] px-4 py-2 rounded-full shadow-lg">
                            <p className='text-base font-bold text-white whitespace-nowrap drop-shadow-sm'>${item.price}</p>
                          </div>
                        </div>
                        
                        <div className="p-5">
                          <Link to={`/cars/${item._id}`}>
                            <h2 className="font-display text-lg font-semibold text-gray-900 mb-4 hover:text-[#DC2626] transition-colors line-clamp-2 min-h-[3.5rem]">
                              {item.title}
                            </h2>
                          </Link>
                          
                          <Divider className="my-3" />
                          
                          <div className='grid grid-cols-2 gap-3 pt-2'>
                            <div className='flex items-center gap-2'>
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                <FaGasPump className="text-[#DC2626] text-sm" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Fuel</p>
                                <p className="text-sm text-gray-900 font-semibold truncate">{item.fuelType}</p>
                              </div>
                            </div>
                            
                            <div className='flex items-center gap-2'>
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                <FaCar className="text-[#DC2626] text-sm" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Body</p>
                                <p className="text-sm text-gray-900 font-semibold truncate">{item.bodyType}</p>
                              </div>
                            </div>
                            
                            <div className='flex items-center gap-2'>
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                <FaTachometerAlt className="text-[#DC2626] text-sm" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Mileage</p>
                                <p className="text-sm text-gray-900 font-semibold truncate">{item.mileage} {item.mileageUnit}</p>
                              </div>
                            </div>
                            
                            <div className='flex items-center gap-2'>
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                <TbSteeringWheel className="text-[#DC2626] text-base" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Transmission</p>
                                <p className="text-sm text-gray-900 font-semibold truncate">{item.vehicleTransmission}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Search;
